/* ===========================================================================
   ihn-faq.js — Inova Professional Help Center (FAQ v2)

   PROGRESSIVE ENHANCEMENT ONLY.

   Every question and every answer is rendered in the server HTML, inside a
   <details> that ships CLOSED. This file does three presentational things and
   nothing else:

     1. opens the row named by the URL hash, on load and on hashchange,
     2. filters rows against the search box using text already in the DOM,
     3. keeps the sticky category bar in sync with the scroll position.

   Nothing here creates content. With JavaScript disabled or blocked the
   accordions still open and close, because <summary> toggling is native and
   the collapse rules are plain CSS on the `open` attribute; only search,
   expand/collapse-all and hash handling are lost. Every question and answer
   stays in the HTML either way.
   =========================================================================== */
(function () {
  'use strict';

  var scope = document.querySelector('[data-ihnq-root]');
  if (!scope) { return; }

  var rows   = [].slice.call(document.querySelectorAll('.ihnq-q'));
  var groups = [].slice.call(document.querySelectorAll('.ihnq-group'));
  var chips  = [].slice.call(document.querySelectorAll('.ihnq-chips a'));
  var input  = document.querySelector('[data-ihnq-search]');
  var clear  = document.querySelector('[data-ihnq-clear]');
  var count  = document.querySelector('[data-ihnq-count]');
  var empty  = document.querySelector('[data-ihnq-empty]');
  var toggle = document.querySelector('[data-ihnq-toggleall]');

  /* ---- 1. hash deep-linking ---------------------------------------------- */

  // Opens the row named by the current #hash. Called on load, again shortly
  // after (some navigations apply the fragment after the deferred script has
  // already run) and on every hashchange, so in-page permalinks and the
  // browser's back/forward buttons both land on an open answer.
  function openByHash(scroll) {
    var h = (window.location.hash || '').replace(/^#/, '');
    if (!h) { return false; }
    var el = null;
    try { el = document.getElementById(h); } catch (e) { el = null; }
    if (el && !el.classList.contains('ihnq-q')) {
      el = el.closest ? el.closest('.ihnq-q') : null;
    }
    if (!el) { return false; }
    rows.forEach(function (r) { r.classList.remove('is-target'); });
    el.open = true;
    el.classList.add('is-target');
    if (scroll) {
      // let layout settle first; the gap under the sticky bar comes from
      // scroll-margin-top in ihn-faq.css, which scrollIntoView() honours, so
      // the offset is not duplicated here.
      window.setTimeout(function () { el.scrollIntoView(); }, 60);
    }
    return true;
  }

  // Rows already arrive closed from the server, so nothing is forced shut here.
  openByHash(true);
  if (toggle) { toggle.classList.add('is-on'); }

  window.addEventListener('hashchange', function () { openByHash(true); });
  window.setTimeout(function () { openByHash(true); }, 350);

  /* ---- 2. search --------------------------------------------------------- */

  var terms = function (s) {
    return String(s).toLowerCase().replace(/[^\p{L}\p{N}\s-]/gu, ' ')
      .split(/\s+/).filter(function (t) { return t.length > 1; });
  };

  var haystack = rows.map(function (r) {
    return (
      (r.getAttribute('data-q') || '') + ' ' + (r.getAttribute('data-a') || '')
    ).toLowerCase();
  });

  function reset() {
    rows.forEach(function (r) { r.classList.remove('is-hidden'); });
    groups.forEach(function (g) { g.classList.remove('is-hidden'); });
    if (empty) { empty.classList.remove('is-on'); }
    if (count) { count.textContent = ''; }
    if (clear) { clear.classList.remove('is-on'); }
  }

  function filter(q) {
    var t = terms(q);
    if (!t.length) { reset(); return; }

    var shown = 0;
    rows.forEach(function (r, i) {
      var hay = haystack[i];
      var hit = t.every(function (term) { return hay.indexOf(term) !== -1; });
      r.classList.toggle('is-hidden', !hit);
      if (hit) { shown++; r.open = true; } else { r.open = false; }
      r.classList.remove('is-target');
    });

    groups.forEach(function (g) {
      var any = g.querySelector('.ihnq-q:not(.is-hidden)');
      g.classList.toggle('is-hidden', !any);
    });

    if (empty) { empty.classList.toggle('is-on', shown === 0); }
    if (clear) { clear.classList.add('is-on'); }
    if (count) {
      count.textContent = shown === 0
        ? 'No questions match “' + q + '”.'
        : shown + (shown === 1 ? ' question matches ' : ' questions match ') + '“' + q + '”.';
    }
  }

  if (input) {
    var timer = null;
    input.addEventListener('input', function () {
      window.clearTimeout(timer);
      var v = input.value;
      timer = window.setTimeout(function () { filter(v); }, 130);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { input.value = ''; reset(); }
    });
  }

  if (clear) {
    clear.addEventListener('click', function () {
      if (input) { input.value = ''; input.focus(); }
      reset();
    });
  }

  /* ---- 3. expand / collapse all ------------------------------------------ */

  if (toggle) {
    toggle.addEventListener('click', function () {
      var expand = toggle.getAttribute('data-state') !== 'expanded';
      rows.forEach(function (r) {
        if (!r.classList.contains('is-hidden')) { r.open = expand; }
      });
      toggle.setAttribute('data-state', expand ? 'expanded' : 'collapsed');
      toggle.textContent = expand ? 'Collapse all' : 'Expand all';
    });
  }

  /* ---- 4. permalink affordance ------------------------------------------- */

  [].slice.call(document.querySelectorAll('.ihnq-link')).forEach(function (a) {
    a.addEventListener('click', function (e) {
      // the link sits inside <summary>; stop it toggling the row
      e.stopPropagation();
      var row = a.closest('.ihnq-q');
      if (row) { row.open = true; }
    });
  });

  /* ---- 5. category bar scrollspy ----------------------------------------- */

  if (chips.length && groups.length && 'IntersectionObserver' in window) {
    var current = null;
    var mark = function (id) {
      if (id === current) { return; }
      current = id;
      chips.forEach(function (c) {
        var on = c.getAttribute('href') === '#' + id;
        if (on) { c.setAttribute('aria-current', 'true'); }
        else { c.removeAttribute('aria-current'); }
      });
    };
    var io = new IntersectionObserver(function (entries) {
      var best = null;
      entries.forEach(function (en) {
        if (!en.isIntersecting) { return; }
        if (!best || en.intersectionRatio > best.intersectionRatio) { best = en; }
      });
      if (best && best.target.id) { mark(best.target.id); }
    }, { rootMargin: '-74px 0px -55% 0px', threshold: [0, 0.01, 0.2] });
    groups.forEach(function (g) { if (g.id) { io.observe(g); } });
  }
})();
